import { useState } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../utils/constants";

const AMOUNT_SCALE_FACTOR = 1e11;
const SLIPPAGE_SCALE_FACTOR = 1e5;

/**
 * Custom hook to encapsulate the Quick Trade (buy/sell) business logic.
 * @param {string} tokenId - The ID of the token to trade.
 * @param {'buy' | 'sell'} tradeType - The current trade type ('buy' or 'sell').
 * @param {string} selectedWalletOrGroupId - The ID of the selected wallet or group.
 * @param {string} amount - The amount to trade (as a string from input).
 * @param {string} slippage - The slippage percentage (as a string from input).
 * @param {Array} walletList - List of all available wallets.
 * @param {Object} mainWallet - The main wallet object.
 * @param {boolean} canTrade - Derived state indicating if the trade button should be enabled.
 * @returns {Object} An object containing the trading state and the quick trade handler.
 */
export const useQuickTradeLogic = (
  tokenId,
  tradeType,
  selectedWalletOrGroupId,
  amount,
  slippage,
  walletList,
  mainWallet,
  canTrade
) => {
  const [isTrading, setIsTrading] = useState(false);

  const handleQuickTrade = async () => {
    if (!canTrade) {
      toast.error(
        "Please ensure a wallet/group is selected, amount is valid, and slippage is within 0-100%."
      );
      return;
    }

    setIsTrading(true);

    const numericAmount = parseFloat(amount) * AMOUNT_SCALE_FACTOR;
    const numericSlippage = parseFloat(slippage);

    let targetWallets = [];

    const selectedWalletObj = walletList.find((wallet) => wallet._id === selectedWalletOrGroupId);
    if (selectedWalletObj) {
      targetWallets.push(selectedWalletObj);
    } else if (selectedWalletOrGroupId === "ODIN_MAIN_WALLET" && mainWallet) {
      targetWallets.push(mainWallet);
    } else {
      const walletsInGroup = walletList.filter((wallet) => wallet.group === selectedWalletOrGroupId);
      if (walletsInGroup.length > 0) {
        targetWallets = walletsInGroup;
      } else {
        toast.error("Selected group contains no wallets or an invalid selection was made.");
        setIsTrading(false);
        return;
      }
    }

    let currentPrice;
    try {
      const priceResp = await fetch(`${ API_BASE_URL }/token/${ tokenId }/price`);
      if (!priceResp.ok) {
        const errorData = await priceResp.json();
        throw new Error(errorData.error || "Failed to fetch token price.");
      }
      const priceData = await priceResp.json();
      currentPrice = priceData.price;
    } catch (error) {
      toast.error(`Error fetching token price: ${ error.message }`);
      setIsTrading(false);
      return;
    }

    const apiEndpoint = tradeType === "buy" ? "quickbuy" : "quicksell";
    const actionVerb = tradeType === "buy" ? "Buying" : "Selling";
    const successVerb = tradeType === "buy" ? "Bought" : "Sold";

    for (const wallet of targetWallets) {
      const toastId = toast.loading(
        `${ actionVerb } ${ amount } ${ tokenId } with ${ wallet.name || wallet._id }...`
      );

      try {
        let chosenWalletData = {};
        if (wallet.serializeIdentity) {
          chosenWalletData = {
            walletData: wallet.serializeIdentity,
            type: "BTC"
          };
        } else if (wallet.privateKey) {
          chosenWalletData = {
            walletData: wallet.privateKey,
            type: "ICP"
          };
        } else {
          throw new Error("Wallet data (serializeIdentity or privateKey) is missing.");
        }

        const payload = {
          ...chosenWalletData,
          tokenId,
          amount: numericAmount,
          settings:
            numericSlippage === 0
              ? [{ slippage: [] }]
              : [
                  {
                    slippage: [[currentPrice, Math.floor(numericSlippage * SLIPPAGE_SCALE_FACTOR)]]
                  }
                ]
        };

        const response = await fetch(`${ API_BASE_URL }/${ apiEndpoint }`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          toast.update(toastId, {
            render: `${ actionVerb } failed for ${ wallet.name || wallet._id }: ${ data.error || "Unknown error" }`,
            type: "error",
            isLoading: false,
            autoClose: 5000
          });
        } else {
          toast.update(toastId, {
            render: `${ successVerb } ${ amount } ${ tokenId } with ${ wallet.name || wallet._id }!`,
            type: "success",
            isLoading: false,
            autoClose: 5000
          });
        }
      } catch (error) {
        console.error(`QuickTrade error for wallet ${ wallet.name || wallet._id }:`, error);
        toast.update(toastId, {
          render: `${ actionVerb } error for ${ wallet.name || wallet._id }: ${ error.message }`,
          type: "error",
          isLoading: false,
          autoClose: 5000
        });
      }
    }
    setIsTrading(false);
  };

  return {
    isTrading,
    handleQuickTrade
  };
};
